import { utils, type OrderLineDisplayTotals } from 'washday-sdk';
import {
  resolveShippingDecision,
  type ShippingAddressCoordinates
} from '@washday/utilities';
import type { IStore } from '../../../interfaces/Store';

/**
 * Returns the display-safe subtotal and the correct taxes i18n key for a
 * given order/form snapshot and store config.
 *
 * Subtotal precedence:
 *   1. displayTotals.subtotal  — explicit Phase-2 field (not yet emitted by SDK)
 *   2. productTotalWithoutDiscount — gross (included) or display-net (over_price) before discount
 *   3. subtotal                — legacy fallback (ambiguous semantics, avoid showing)
 *   4. 0
 */
export function resolveOrderDisplayTotals(
  orderOrFormValues: {
    displayTotals?: { subtotal?: number } | null;
    productTotalWithoutDiscount?: number | null;
    subtotal?: number | null;
  },
  store?: Pick<IStore, 'taxesType'> | null,
): { subtotalDisplay: number; taxesLabelKey: 'taxesIncluded' | 'taxesTotal' } {
  const subtotalDisplay =
    orderOrFormValues.displayTotals?.subtotal ??
    orderOrFormValues.productTotalWithoutDiscount ??
    orderOrFormValues.subtotal ??
    0;

  const taxesLabelKey: 'taxesIncluded' | 'taxesTotal' =
    store?.taxesType === 'included' ? 'taxesIncluded' : 'taxesTotal';

  return { subtotalDisplay, taxesLabelKey };
}

export interface OrderTotalsFormValues {
  orderType?: 'standard' | 'return';
  returnSourceOrderId?: string;
  returnItemsChargeMode?: 'no_charge' | 'charge';
  products: Array<{ quantity?: number; qty?: number; [key: string]: unknown }>;
  buyAndGetProducts: Array<{ quantity?: number; qty?: number; [key: string]: unknown }>;
  delivery?: boolean;
  pickup?: boolean;
  address?: ShippingAddressCoordinates | null;
  customerInfo?: unknown;
  discountCodeObj?: unknown;
  loyaltyDiscountAmount?: number | null;
}

export interface OrderTotalsResult {
  lineDisplayTotals: OrderLineDisplayTotals[];
  displayTotals?: { subtotal?: number; discount?: number; creditApplied?: number; redeemPointsApplied?: number };
  totalQuantity: number;
  customerDiscount: unknown;
  productTotal: number;
  taxesTotal: number;
  total: number;
  productTotalWithoutDiscount: number;
  totalDiscountAmount: number;
  shippingServiceTotal: number;
  appliedOrderDiscounts: Record<string, unknown>;
  subtotal: number;
}

/**
 * Calculates order totals from form values using the SDK's calculateOrderTotal.
 * Used by the NewOrder page to update total, subtotal, taxes, etc.
 */
export function calculateOrderTotalsFromFormValues(
  formValues: OrderTotalsFormValues,
  store: IStore | null | undefined,
  storeDiscounts: unknown[] = []
): OrderTotalsResult {
  const customerAddress = (
    formValues.customerInfo as { address?: ShippingAddressCoordinates | null } | null | undefined
  )?.address;
  const tmpOrder = {
    ...formValues,
    products: (formValues.products ?? []).map((product) => ({
      ...product,
      qty: product.quantity ?? product.qty
    })),
    buyAndGetProducts: (formValues.buyAndGetProducts ?? []).map((product) => ({
      ...product,
      qty: product.quantity ?? product.qty
    }))
  };

  const result = utils.calculateOrderTotal(
    tmpOrder,
    { customer: formValues.customerInfo as any },
    store as unknown as Parameters<typeof utils.calculateOrderTotal>[2],
    resolveShippingDecision({
      delivery: Boolean(formValues.delivery),
      pickup: Boolean(formValues.pickup),
      shippingServiceCost: store?.orderPageConfig?.shippingServiceCost,
      freeShippingRadiusKm: store?.orderPageConfig?.freeShippingRadiusKm,
      storeMapLocation: store?.mapLocation,
      address: formValues.address ?? customerAddress
    }).shouldCharge,
    storeDiscounts,
    formValues.discountCodeObj,
    formValues.loyaltyDiscountAmount ?? 0
  );

  return result as OrderTotalsResult;
}
